import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { KaraokeSongCreator } from './../Karaoke/KaraokeSongCreator'
import { SendQuestionToUsers as BLSendQuestionToUsers, GetAllResults, ResetCurrentDynamic as BLResetCurrentDynamic, SendResultsToScreen } from './../../../../utilities/BL/Zones'
import { SecondaryBase } from './../../Commons/SecondaryBase'
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage'

import { GetSongs as BLGetSongs, SaveSong as BLSaveSong, DeleteSong as BLDeleteSong } from './../../../../utilities/BL/Admin/Games/Karaoke'

import { Confirm } from './../../../Commons/Modals/Confirm'


const getBlockDurationMs = (block) => {
  if (!block) return 0
  if (block.type === 'instrumental' || block.mode === 'uniform') return Math.max(0, Number(block.durationMs) || 0)
  return (block.parts || []).reduce((acc, p) => acc + Math.max(0, Number(p.durationMs) || 0), 0)
}

const getBlockDisplayText = (block) => {
  if (!block) return ""
  if (block.type === 'instrumental') return block.display === 'icon' ? "♪" : ""
  if (block.mode === 'uniform') return block.text || ""
  return (block.parts || []).map(p => p.text || "").join("")
}

const getFillProgress = (block, elapsedMs) => {
  const total = getBlockDurationMs(block)
  if (total <= 0) return 0
  if (block.type === 'instrumental') return 1 
  return Math.min(Math.max(elapsedMs, 0), total) / total 
}

const formatTime = (ms) => {
  if (!ms || ms < 0) return "00:00"
  const s = Math.floor(ms / 1000)
  return `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`
}

const generateUuid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `uuid_${Date.now()}_${Math.random()}`)


/**
 * ==============================================================================
 * COMPONENTE: LYRICS GENERATOR (ADMIN DASHBOARD)
 * ------------------------------------------------------------------------------
 * Controla el reproductor, la lista de reproducción y los modales.
 * ==============================================================================
 */
const LyricsGenerator = ({ getOutOfTheDynamic }) => {
    const navigate = useNavigate()
    const { showMessage } = useFloatMessage()

    // -- ESTADOS DE DATOS --
    const [songs, setSongs] = useState([])
    const [selectedSongId, setSelectedSongId] = useState(null)
    const [isCreatorOpen, setIsCreatorOpen] = useState(false)

    // -- ESTADOS DEL REPRODUCTOR --
    const [isPlaying, setIsPlaying] = useState(false)
    const [activeBlockIndex, setActiveBlockIndex] = useState(0)
    const [elapsedInBlockMs, setElapsedInBlockMs] = useState(0)

    // -- ESTADOS DE MODAL DE CONFIRMACIÓN --
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [confirmData, setConfirmData] = useState(null) // { message, action, ... }

    // -- REFS --
    const rafIdRef = useRef(null)
    const lastFrameTimeRef = useRef(0)
    const lyricsListRef = useRef(null) // Ref para el scroll de la lista

    // -- COMPUTADOS --
    const selectedSong = useMemo(() => songs.find(s => s.id === selectedSongId), [songs, selectedSongId])
    const blocks = useMemo(() => selectedSong?.lyrics || [], [selectedSong])
    const activeBlock = blocks[activeBlockIndex] || null
    const displayText = useMemo(() => getBlockDisplayText(activeBlock), [activeBlock])
    const fillProgress = getFillProgress(activeBlock, elapsedInBlockMs)

    const totalDurationMs = useMemo(() => blocks.reduce((acc, b) => acc + getBlockDurationMs(b), 0), [blocks])
    const currentPositionMs = useMemo(() => {
        let prev = 0
        for (let i = 0; i < activeBlockIndex; i++) prev += getBlockDurationMs(blocks[i])
        return prev + elapsedInBlockMs
    }, [blocks, activeBlockIndex, elapsedInBlockMs])

    /** Carga inicial de canciones */
    const loadSongs = async () => {
        const response = await BLGetSongs()
        if (response.success) {
            setSongs(response.data || [])
            // Si la canción seleccionada fue borrada, limpiar selección
            if (selectedSongId && response.data && !response.data.find(s => s.id === selectedSongId)) {
                setSelectedSongId(null)
            }
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: response.message })
        }
    }

    useEffect(() => {
        loadSongs()
        return () => { if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current) }
    }, [])

    /** * Callback cuando el Creador guarda una canción.
     * Guarda en BD -> Recarga Lista -> Mantiene selección.
     */
    const onSongsChanged = async (updatedSongsListOrSingleSong, newSelectedId) => {
        let songToSave = null
        if (Array.isArray(updatedSongsListOrSingleSong)) {
            songToSave = updatedSongsListOrSingleSong.find(s => s.id === newSelectedId)
        } else {
            songToSave = updatedSongsListOrSingleSong
        }

        if (songToSave) {
            const response = await BLSaveSong(songToSave)
            if (response.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Canción guardada y actualizada.' })
                await loadSongs() // Recarga crítica para actualizar el reproductor
                if (newSelectedId) setSelectedSongId(newSelectedId)
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: response.message })
            }
        }
    }

    /** Eliminar canción con Confirmación */
    const requestDeleteSong = () => {
        if (!selectedSongId) return

        setConfirmData({
            message: `¿Estás seguro de eliminar "${selectedSong?.name}"?`,
            textYesButton: "Eliminar",
            textNoButton: "Cancelar",
            actionToYesButton: async () => {
                const response = await BLDeleteSong(selectedSongId)
                if (response.success) {
                    await loadSongs()
                    setSelectedSongId(null)
                    stopPlayback()
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Canción eliminada.' })
                    return { success: true }
                } else {
                    return { success: false, message: response.message }
                }
            }
        })
        setShowConfirmModal(true)
    }

    // -- CONTROLES DEL PLAYER --

    // Al cambiar de canción, detener y resetear
    useEffect(() => {
        stopPlayback()
    }, [selectedSongId])

    const stopPlayback = () => {
        setIsPlaying(false)
        setActiveBlockIndex(0)
        setElapsedInBlockMs(0)
        lastFrameTimeRef.current = 0
        if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current)
    }

    const playPlayback = () => {
        if (!blocks.length) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Canción vacía' })
        setIsPlaying(true)
    }

    const pausePlayback = () => {
        setIsPlaying(false)
        lastFrameTimeRef.current = 0
    }

    const goToStart = () => {
        // Detener resetea el índice a 0
        stopPlayback()
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Inicio de la canción' })
    }

    const goToEnd = () => {
        if (!blocks.length) return
        setElapsedInBlockMs(0)
        setActiveBlockIndex(blocks.length - 1)
        // Opcional: pausar al ir al final
        setIsPlaying(false)
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Final de la canción' })
    }

    const goToNextBlock = () => {
        if (!blocks.length) return
        setElapsedInBlockMs(0)
        setActiveBlockIndex(prev => (prev + 1) >= blocks.length ? 0 : prev + 1)
    }

    const goToPrevBlock = () => {
        if (!blocks.length) return
        setElapsedInBlockMs(0)
        setActiveBlockIndex(prev => (prev - 1) < 0 ? blocks.length - 1 : prev - 1)
    }

    /** Función para saltar a una línea específica desde la lista derecha */
    const handleJumpToLine = (index) => {
        setElapsedInBlockMs(0)
        setActiveBlockIndex(index)
        // Si quieres que al saltar empiece a tocar automáticamente, descomenta:
        // setIsPlaying(true) 
    }

    /** Loop de Animación */
    useEffect(() => {
        if (!isPlaying || !activeBlock) return

        const tick = (now) => {
            if (!lastFrameTimeRef.current) lastFrameTimeRef.current = now
            const delta = now - lastFrameTimeRef.current
            lastFrameTimeRef.current = now

            setElapsedInBlockMs(prev => {
                const duration = getBlockDurationMs(activeBlock)
                const nextElapsed = prev + delta

                if (duration > 0 && nextElapsed >= duration) {
                    const nextIdx = activeBlockIndex + 1
                    if (nextIdx >= blocks.length) {
                        setIsPlaying(false)
                        return duration
                    }
                    setActiveBlockIndex(nextIdx)
                    lastFrameTimeRef.current = 0
                    return 0
                }
                return nextElapsed
            })
            rafIdRef.current = requestAnimationFrame(tick)
        }
        rafIdRef.current = requestAnimationFrame(tick)
        return () => cancelAnimationFrame(rafIdRef.current)
    }, [isPlaying, activeBlockIndex, activeBlock, blocks.length])

    // Auto-scroll de la lista
    useEffect(() => {
        if (lyricsListRef.current) {
            const activeItem = lyricsListRef.current.children[activeBlockIndex]
            if (activeItem) {
                activeItem.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
        }
    }, [activeBlockIndex])


    return (
        <SecondaryBase urlToGo={'/admin/dashboard'}>
            <div className='LyricsGenerator'>
                <p className='LyricsGenerator__Text--Title'>Karaoke</p>

                {/* 1. BARRA DE CONTROLES */}
                <div className='LyricsGenerator__Playlist'>
                    <p className='LyricsGenerator__Text--Center'>Canción:</p>
                    <div style={{ display: 'flex', gap: 10, flex: 1 }}>
                        <select className='LyricsGenerator__Playlist__List' value={selectedSongId || ""} onChange={(e) => setSelectedSongId(e.target.value)}>
                            <option value="">-- Selecciona una canción --</option>
                            {songs.map(s => <option key={s.id} value={s.id}>{s.name} - {s.artist}</option>)}
                        </select>
                        {selectedSongId && (
                            <button type="button" className='Btn__Adsl--RedOutlineTransparent' onClick={requestDeleteSong} title="Eliminar canción">
                                <i className="bi bi-trash"></i>
                            </button>
                        )}
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    {/* 2. PREVIEW VISUAL (IZQUIERDA) */}
                    <div style={{ flex: 1, minWidth: '300px' }}>
                        <div className='LyricsGenerator__Player__LyricsContainer'>
                            <div className='LyricsGenerator__Player__LyricsContainer__LyricsSubcontainer'>
                                <div className='LyricsGenerator__Player__LyricsContainer__LyricsSubcontainer__BaseLyrics'>
                                    {displayText || "..."}
                                </div>
                                <div className='LyricsGenerator__Player__LyricsContainer__LyricsSubcontainer__FillLyrics' style={{ width: `${(fillProgress * 100).toFixed(2)}%` }}>
                                    {displayText || ""}
                                </div>
                            </div>
                        </div>

                        {/* INFO Y RELOJ */}
                        <div className='LyricsGenerator__Player__Data'>
                            <div>Bloque: {blocks.length ? `${activeBlockIndex + 1} / ${blocks.length}` : "-"}</div>
                            <div>{formatTime(currentPositionMs)} / {formatTime(totalDurationMs)}</div>
                        </div>

                        {/* BOTONES */}
                        <div className='LyricsGenerator__Player__Buttons'>
                            <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={goToStart}><i className='bi bi-chevron-bar-left'></i> Inicio</button>
                            <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={goToPrevBlock}><i className='bi bi-chevron-left'></i> Anterior</button>
                            {!isPlaying ? (
                                <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={playPlayback}><i className='bi bi-play'></i> Reproducir</button>
                            ) : (
                                <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={pausePlayback}><i className='bi bi-pause'></i> Pausa</button>
                            )}
                            <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={stopPlayback}><i className='bi bi-stop'></i> Detener</button>
                            <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={goToNextBlock}><i className='bi bi-chevron-right'></i> Siguiente</button>
                            <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={goToEnd}><i className='bi bi-chevron-bar-right'></i> Fin</button>
                        </div>
                    </div>

                    {/* 3. LISTA DE LETRAS (DERECHA - NUEVO) */}
                    {/* Se muestra solo si hay canción seleccionada */}
                    {selectedSongId && (
                        <div className='LyricsGenerator__LyricsListContainer Element--Shadow' style={{ width: '300px', maxHeight: '400px', overflowY: 'auto', background: '#fff', borderRadius: 8, padding: 10 }}>
                            <p style={{ fontWeight: 'bold', textAlign: 'center', marginBottom: 10 }}>Letra de la Canción</p>
                            <ul ref={lyricsListRef} style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {blocks.map((block, index) => {
                                    const isActive = index === activeBlockIndex
                                    const text = block.type === 'instrumental' ? '🎵 Instrumental' : (block.mode === 'uniform' ? block.text : block.parts.map(p => p.text).join(""))
                                    return (
                                        <li
                                            key={block.id || index}
                                            onClick={() => handleJumpToLine(index)}
                                            style={{
                                                padding: '8px',
                                                borderBottom: '1px solid #eee',
                                                cursor: 'pointer',
                                                backgroundColor: isActive ? '#ffebee' : 'transparent',
                                                fontWeight: isActive ? 'bold' : 'normal',
                                                color: isActive ? '#d32f2f' : '#333',
                                                borderRadius: 4
                                            }}
                                        >
                                            <span style={{ marginRight: 5, fontSize: '0.8em', color: '#999' }}>#{index + 1}</span>
                                            {text}
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>
                    )}
                </div>

                <div style={{ marginTop: 20, textAlign: 'center' }}>
                    {isCreatorOpen ? (
                        <button className='Btn__Adsl--RedOutlineTransparent' onClick={() => setIsCreatorOpen(false)}>Cerrar editor</button>
                    ) : (
                        <button className='Btn__Adsl--RedOutlineTransparent' onClick={() => setIsCreatorOpen(true)}>Abrir editor</button>
                    )}
                </div>

                {/* 4. MODAL DEL CREADOR */}
                {isCreatorOpen && (
                    <div className='LyricsGenerator__Editor'>
                        <KaraokeSongCreator
                            songs={songs}
                            selectedSongId={selectedSongId}
                            onSongsChanged={onSongsChanged}
                            onRequestClose={() => setIsCreatorOpen(false)}
                            generateUuid={generateUuid}
                        />
                    </div>
                )}

                {/* 5. MODAL DE CONFIRMACIÓN */}
                {showConfirmModal && (
                    <Confirm
                        message={confirmData?.message}
                        textYesButton={confirmData?.textYesButton}
                        textNoButton={confirmData?.textNoButton}
                        actionToYesButton={confirmData?.actionToYesButton}
                        setShowModal={setShowConfirmModal}
                        setConfirmData={setConfirmData}
                    />
                )}
            </div>
        </SecondaryBase>
    );
};

export { LyricsGenerator };









// Funcional
// import React, { useState, useEffect, useRef, useMemo } from 'react'
// import { useNavigate } from 'react-router-dom'

// import { KaraokeSongCreator } from './../Karaoke/KaraokeSongCreator'
// import { SendQuestionToUsers as BLSendQuestionToUsers, GetAllResults, ResetCurrentDynamic as BLResetCurrentDynamic, SendResultsToScreen } from './../../../../utilities/BL/Zones'
// import { SecondaryBase } from './../../Commons/SecondaryBase'
// import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage'

// import { GetSongs as BLGetSongs, SaveSong as BLSaveSong, DeleteSong as BLDeleteSong } from './../../../../utilities/BL/Admin/Games/Karaoke'

// import { Confirm } from './../../../Commons/Modals/Confirm'


// const getBlockDurationMs = (block) => {
//   if (!block) return 0
//   if (block.type === 'instrumental' || block.mode === 'uniform') return Math.max(0, Number(block.durationMs) || 0)
//   return (block.parts || []).reduce((acc, p) => acc + Math.max(0, Number(p.durationMs) || 0), 0)
// }

// const getBlockDisplayText = (block) => {
//   if (!block) return ""
//   if (block.type === 'instrumental') return block.display === 'icon' ? "♪" : ""
//   if (block.mode === 'uniform') return block.text || ""
//   return (block.parts || []).map(p => p.text || "").join("")
// }

// const getFillProgress = (block, elapsedMs) => {
//   const total = getBlockDurationMs(block)
//   if (total <= 0) return 0
//   if (block.type === 'instrumental') return 1 
//   return Math.min(Math.max(elapsedMs, 0), total) / total 
// }

// const formatTime = (ms) => {
//   if (!ms || ms < 0) return "00:00"
//   const s = Math.floor(ms / 1000)
//   return `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`
// }

// const generateUuid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `uuid_${Date.now()}_${Math.random()}`)


// /**
//  * ==============================================================================
//  * COMPONENTE: LYRICS GENERATOR (ADMIN DASHBOARD)
//  * ------------------------------------------------------------------------------
//  * Controla el reproductor, la lista de reproducción y los modales.
//  * ==============================================================================
//  */
// const LyricsGenerator = ({ getOutOfTheDynamic }) => {
//     const navigate = useNavigate()
//     const { showMessage } = useFloatMessage()

//     // -- ESTADOS DE DATOS --
//     const [songs, setSongs] = useState([])
//     const [selectedSongId, setSelectedSongId] = useState(null)
//     const [isCreatorOpen, setIsCreatorOpen] = useState(false)

//     // -- ESTADOS DEL REPRODUCTOR --
//     const [isPlaying, setIsPlaying] = useState(false)
//     const [activeBlockIndex, setActiveBlockIndex] = useState(0)
//     const [elapsedInBlockMs, setElapsedInBlockMs] = useState(0)

//     // -- ESTADOS DE MODAL DE CONFIRMACIÓN --
//     const [showConfirmModal, setShowConfirmModal] = useState(false)
//     const [confirmData, setConfirmData] = useState(null) // { message, action, ... }

//     // -- REFS --
//     const rafIdRef = useRef(null)
//     const lastFrameTimeRef = useRef(0)
//     const lyricsListRef = useRef(null) // Ref para el scroll de la lista

//     // -- COMPUTADOS --
//     const selectedSong = useMemo(() => songs.find(s => s.id === selectedSongId), [songs, selectedSongId])
//     const blocks = useMemo(() => selectedSong?.lyrics || [], [selectedSong])
//     const activeBlock = blocks[activeBlockIndex] || null
//     const displayText = useMemo(() => getBlockDisplayText(activeBlock), [activeBlock])
//     const fillProgress = getFillProgress(activeBlock, elapsedInBlockMs)

//     const totalDurationMs = useMemo(() => blocks.reduce((acc, b) => acc + getBlockDurationMs(b), 0), [blocks])
//     const currentPositionMs = useMemo(() => {
//         let prev = 0
//         for (let i = 0; i < activeBlockIndex; i++) prev += getBlockDurationMs(blocks[i])
//         return prev + elapsedInBlockMs
//     }, [blocks, activeBlockIndex, elapsedInBlockMs])

//     /** Carga inicial de canciones */
//     const loadSongs = async () => {
//         const response = await BLGetSongs()
//         if (response.success) {
//             setSongs(response.data || [])
//             // Si la canción seleccionada fue borrada, limpiar selección
//             if (selectedSongId && response.data && !response.data.find(s => s.id === selectedSongId)) {
//                 setSelectedSongId(null)
//             }
//         } else {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: response.message })
//         }
//     }

//     useEffect(() => {
//         loadSongs()
//         return () => { if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current) }
//     }, [])

//     /** * Callback cuando el Creador guarda una canción.
//      * Guarda en BD -> Recarga Lista -> Mantiene selección.
//      */
//     const onSongsChanged = async (updatedSongsListOrSingleSong, newSelectedId) => {
//         let songToSave = null
//         if (Array.isArray(updatedSongsListOrSingleSong)) {
//             songToSave = updatedSongsListOrSingleSong.find(s => s.id === newSelectedId)
//         } else {
//             songToSave = updatedSongsListOrSingleSong
//         }

//         if (songToSave) {
//             const response = await BLSaveSong(songToSave)
//             if (response.success) {
//                 showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Canción guardada y actualizada.' })
//                 await loadSongs() // Recarga crítica para actualizar el reproductor
//                 if (newSelectedId) setSelectedSongId(newSelectedId)
//             } else {
//                 showMessage({ type: MESSAGE_TYPE.BAD, text: response.message })
//             }
//         }
//     }

//     /** Eliminar canción con Confirmación */
//     const requestDeleteSong = () => {
//         if (!selectedSongId) return

//         setConfirmData({
//             message: `¿Estás seguro de eliminar "${selectedSong?.name}"?`,
//             textYesButton: "Eliminar",
//             textNoButton: "Cancelar",
//             actionToYesButton: async () => {
//                 const response = await BLDeleteSong(selectedSongId)
//                 if (response.success) {
//                     await loadSongs()
//                     setSelectedSongId(null)
//                     stopPlayback()
//                     showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Canción eliminada.' })
//                     return { success: true }
//                 } else {
//                     return { success: false, message: response.message }
//                 }
//             }
//         })
//         setShowConfirmModal(true)
//     }

//     // -- CONTROLES DEL PLAYER --

//     // Al cambiar de canción, detener y resetear
//     useEffect(() => {
//         stopPlayback()
//     }, [selectedSongId])

//     const stopPlayback = () => {
//         setIsPlaying(false)
//         setActiveBlockIndex(0)
//         setElapsedInBlockMs(0)
//         lastFrameTimeRef.current = 0
//         if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current)
//     }

//     const playPlayback = () => {
//         if (!blocks.length) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Canción vacía' })
//         setIsPlaying(true)
//     }

//     const pausePlayback = () => {
//         setIsPlaying(false)
//         lastFrameTimeRef.current = 0
//     }

//     const goToStart = () => {
//         // Detener resetea el índice a 0
//         stopPlayback()
//         showMessage({ type: MESSAGE_TYPE.INFO, text: 'Inicio de la canción' })
//     }

//     const goToEnd = () => {
//         if (!blocks.length) return
//         setElapsedInBlockMs(0)
//         setActiveBlockIndex(blocks.length - 1)
//         // Opcional: pausar al ir al final
//         setIsPlaying(false)
//         showMessage({ type: MESSAGE_TYPE.INFO, text: 'Final de la canción' })
//     }

//     const goToNextBlock = () => {
//         if (!blocks.length) return
//         setElapsedInBlockMs(0)
//         setActiveBlockIndex(prev => (prev + 1) >= blocks.length ? 0 : prev + 1)
//     }

//     const goToPrevBlock = () => {
//         if (!blocks.length) return
//         setElapsedInBlockMs(0)
//         setActiveBlockIndex(prev => (prev - 1) < 0 ? blocks.length - 1 : prev - 1)
//     }

//     /** Función para saltar a una línea específica desde la lista derecha */
//     const handleJumpToLine = (index) => {
//         setElapsedInBlockMs(0)
//         setActiveBlockIndex(index)
//         // Si quieres que al saltar empiece a tocar automáticamente, descomenta:
//         // setIsPlaying(true) 
//     }

//     /** Loop de Animación */
//     useEffect(() => {
//         if (!isPlaying || !activeBlock) return

//         const tick = (now) => {
//             if (!lastFrameTimeRef.current) lastFrameTimeRef.current = now
//             const delta = now - lastFrameTimeRef.current
//             lastFrameTimeRef.current = now

//             setElapsedInBlockMs(prev => {
//                 const duration = getBlockDurationMs(activeBlock)
//                 const nextElapsed = prev + delta

//                 if (duration > 0 && nextElapsed >= duration) {
//                     const nextIdx = activeBlockIndex + 1
//                     if (nextIdx >= blocks.length) {
//                         setIsPlaying(false)
//                         return duration
//                     }
//                     setActiveBlockIndex(nextIdx)
//                     lastFrameTimeRef.current = 0
//                     return 0
//                 }
//                 return nextElapsed
//             })
//             rafIdRef.current = requestAnimationFrame(tick)
//         }
//         rafIdRef.current = requestAnimationFrame(tick)
//         return () => cancelAnimationFrame(rafIdRef.current)
//     }, [isPlaying, activeBlockIndex, activeBlock, blocks.length])

//     // Auto-scroll de la lista
//     useEffect(() => {
//         if (lyricsListRef.current) {
//             const activeItem = lyricsListRef.current.children[activeBlockIndex]
//             if (activeItem) {
//                 activeItem.scrollIntoView({ behavior: 'smooth', block: 'center' })
//             }
//         }
//     }, [activeBlockIndex])


//     return (
//         <SecondaryBase urlToGo={'/admin/dashboard'}>
//             <div className='LyricsGenerator'>
//                 <p className='LyricsGenerator__Text--Title'>Karaoke</p>

//                 {/* 1. BARRA DE CONTROLES */}
//                 <div className='LyricsGenerator__Playlist'>
//                     <p className='LyricsGenerator__Text--Center'>Canción:</p>
//                     <div style={{ display: 'flex', gap: 10, flex: 1 }}>
//                         <select className='LyricsGenerator__Playlist__List' value={selectedSongId || ""} onChange={(e) => setSelectedSongId(e.target.value)}>
//                             <option value="">-- Selecciona una canción --</option>
//                             {songs.map(s => <option key={s.id} value={s.id}>{s.name} - {s.artist}</option>)}
//                         </select>
//                         {selectedSongId && (
//                             <button type="button" className='Btn__Adsl--RedOutlineTransparent' onClick={requestDeleteSong} title="Eliminar canción">
//                                 <i className="bi bi-trash"></i>
//                             </button>
//                         )}
//                     </div>
//                 </div>

//                 <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
//                     {/* 2. PREVIEW VISUAL (IZQUIERDA) */}
//                     <div style={{ flex: 1, minWidth: '300px' }}>
//                         <div className='LyricsGenerator__Player__LyricsContainer'>
//                             <div className='LyricsGenerator__Player__LyricsContainer__LyricsSubcontainer'>
//                                 <div className='LyricsGenerator__Player__LyricsContainer__LyricsSubcontainer__BaseLyrics'>
//                                     {displayText || "..."}
//                                 </div>
//                                 <div className='LyricsGenerator__Player__LyricsContainer__LyricsSubcontainer__FillLyrics' style={{ width: `${(fillProgress * 100).toFixed(2)}%` }}>
//                                     {displayText || ""}
//                                 </div>
//                             </div>
//                         </div>

//                         {/* INFO Y RELOJ */}
//                         <div className='LyricsGenerator__Player__Data'>
//                             <div>Bloque: {blocks.length ? `${activeBlockIndex + 1} / ${blocks.length}` : "-"}</div>
//                             <div>{formatTime(currentPositionMs)} / {formatTime(totalDurationMs)}</div>
//                         </div>

//                         {/* BOTONES */}
//                         <div className='LyricsGenerator__Player__Buttons'>
//                             <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={goToStart}><i className='bi bi-chevron-bar-left'></i> Inicio</button>
//                             <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={goToPrevBlock}><i className='bi bi-chevron-left'></i> Anterior</button>
//                             {!isPlaying ? (
//                                 <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={playPlayback}><i className='bi bi-play'></i> Reproducir</button>
//                             ) : (
//                                 <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={pausePlayback}><i className='bi bi-pause'></i> Pausa</button>
//                             )}
//                             <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={stopPlayback}><i className='bi bi-stop'></i> Detener</button>
//                             <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={goToNextBlock}><i className='bi bi-chevron-right'></i> Siguiente</button>
//                             <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={goToEnd}><i className='bi bi-chevron-bar-right'></i> Fin</button>
//                         </div>
//                     </div>

//                     {/* 3. LISTA DE LETRAS (DERECHA - NUEVO) */}
//                     {/* Se muestra solo si hay canción seleccionada */}
//                     {selectedSongId && (
//                         <div className='LyricsGenerator__LyricsListContainer Element--Shadow' style={{ width: '300px', maxHeight: '400px', overflowY: 'auto', background: '#fff', borderRadius: 8, padding: 10 }}>
//                             <p style={{ fontWeight: 'bold', textAlign: 'center', marginBottom: 10 }}>Letra de la Canción</p>
//                             <ul ref={lyricsListRef} style={{ listStyle: 'none', padding: 0, margin: 0 }}>
//                                 {blocks.map((block, index) => {
//                                     const isActive = index === activeBlockIndex
//                                     const text = block.type === 'instrumental' ? '🎵 Instrumental' : (block.mode === 'uniform' ? block.text : block.parts.map(p => p.text).join(""))
//                                     return (
//                                         <li
//                                             key={block.id || index}
//                                             onClick={() => handleJumpToLine(index)}
//                                             style={{
//                                                 padding: '8px',
//                                                 borderBottom: '1px solid #eee',
//                                                 cursor: 'pointer',
//                                                 backgroundColor: isActive ? '#ffebee' : 'transparent',
//                                                 fontWeight: isActive ? 'bold' : 'normal',
//                                                 color: isActive ? '#d32f2f' : '#333',
//                                                 borderRadius: 4
//                                             }}
//                                         >
//                                             <span style={{ marginRight: 5, fontSize: '0.8em', color: '#999' }}>#{index + 1}</span>
//                                             {text}
//                                         </li>
//                                     )
//                                 })}
//                             </ul>
//                         </div>
//                     )}
//                 </div>

//                 <div style={{ marginTop: 20, textAlign: 'center' }}>
//                     {isCreatorOpen ? (
//                         <button className='Btn__Adsl--RedOutlineTransparent' onClick={() => setIsCreatorOpen(false)}>Cerrar editor</button>
//                     ) : (
//                         <button className='Btn__Adsl--RedOutlineTransparent' onClick={() => setIsCreatorOpen(true)}>Abrir editor</button>
//                     )}
//                 </div>

//                 {/* 4. MODAL DEL CREADOR */}
//                 {isCreatorOpen && (
//                     <div className='LyricsGenerator__Editor'>
//                         <KaraokeSongCreator
//                             songs={songs}
//                             selectedSongId={selectedSongId}
//                             onSongsChanged={onSongsChanged}
//                             onRequestClose={() => setIsCreatorOpen(false)}
//                             generateUuid={generateUuid}
//                         />
//                     </div>
//                 )}

//                 {/* 5. MODAL DE CONFIRMACIÓN */}
//                 {showConfirmModal && (
//                     <Confirm
//                         message={confirmData?.message}
//                         textYesButton={confirmData?.textYesButton}
//                         textNoButton={confirmData?.textNoButton}
//                         actionToYesButton={confirmData?.actionToYesButton}
//                         setShowModal={setShowConfirmModal}
//                         setConfirmData={setConfirmData}
//                     />
//                 )}
//             </div>
//         </SecondaryBase>
//     );
// };

// export { LyricsGenerator };








































// Funcional
// /**
//  * ==============================================================================
//  * HELPERS (Cálculos de tiempo y formato)
//  * ==============================================================================
//  */

// const getBlockDurationMs = (block) => {
//   if (!block) return 0
//   if (block.type === 'instrumental' || block.mode === 'uniform') return Math.max(0, Number(block.durationMs) || 0)
//   return (block.parts || []).reduce((acc, p) => acc + Math.max(0, Number(p.durationMs) || 0), 0)
// }

// const getBlockDisplayText = (block) => {
//   if (!block) return ""
//   if (block.type === 'instrumental') return block.display === 'icon' ? "♪" : ""
//   if (block.mode === 'uniform') return block.text || ""
//   return (block.parts || []).map(p => p.text || "").join("")
// }

// const getFillProgress = (block, elapsedMs) => {
//   const total = getBlockDurationMs(block)
//   if (total <= 0) return 0
//   if (block.type === 'instrumental') return 1 
//   return Math.min(Math.max(elapsedMs, 0), total) / total 
// }

// const formatTime = (ms) => {
//   if (!ms || ms < 0) return "00:00"
//   const s = Math.floor(ms / 1000)
//   return `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`
// }

// const generateUuid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `uuid_${Date.now()}_${Math.random()}`)

// /**
//  * ==============================================================================
//  * COMPONENTE PRINCIPAL: LYRICS GENERATOR (ADMINISTRADOR)
//  * ------------------------------------------------------------------------------
//  * Permite crear, guardar y previsualizar canciones conectando con la BD.
//  * ==============================================================================
//  */
// const LyricsGenerator = ({ getOutOfTheDynamic }) => {
//   const navigate = useNavigate()
//   const { showMessage } = useFloatMessage()

//   // -- ESTADOS DE DATOS --
//   const [songs, setSongs] = useState([])
//   const [selectedSongId, setSelectedSongId] = useState(null)
//   const [isCreatorOpen, setIsCreatorOpen] = useState(false)

//   // -- ESTADOS DEL PLAYER --
//   const [isPlaying, setIsPlaying] = useState(false)
//   const [activeBlockIndex, setActiveBlockIndex] = useState(0)
//   const [elapsedInBlockMs, setElapsedInBlockMs] = useState(0)

//   // -- REFS PARA ANIMACIÓN --
//   const rafIdRef = useRef(null)
//   const lastFrameTimeRef = useRef(0)

//   // -- COMPUTADOS (MEMO) --
//   const selectedSong = useMemo(() => songs.find(s => s.id === selectedSongId), [songs, selectedSongId])
//   const blocks = useMemo(() => selectedSong?.lyrics || [], [selectedSong])
//   const activeBlock = blocks[activeBlockIndex] || null
//   const displayText = useMemo(() => getBlockDisplayText(activeBlock), [activeBlock])
//   const fillProgress = getFillProgress(activeBlock, elapsedInBlockMs)

//   // Cálculos para el RELOJ
//   const totalDurationMs = useMemo(() => blocks.reduce((acc, b) => acc + getBlockDurationMs(b), 0), [blocks])
//   const currentPositionMs = useMemo(() => {
//     let prev = 0
//     for (let i = 0; i < activeBlockIndex; i++) prev += getBlockDurationMs(blocks[i])
//     return prev + elapsedInBlockMs
//   }, [blocks, activeBlockIndex, elapsedInBlockMs])

//   /**
//    * Carga inicial de canciones desde la BD (Punto 2)
//    */
//   const loadSongs = async () => {
//     const response = await BLGetSongs()
//     if (response.success) {
//       setSongs(response.data || [])
//       // Si la canción seleccionada ya no existe (fue borrada), reseteamos selección
//       if (selectedSongId && response.data && !response.data.find(s => s.id === selectedSongId)) {
//         setSelectedSongId(null)
//       }
//     } else {
//       showMessage({ type: MESSAGE_TYPE.BAD, text: response.message })
//     }
//   }

//   useEffect(() => {
//     loadSongs()
//     // Cleanup de animación al desmontar
//     return () => { if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current) }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [])

//   /** * Manejo de cambios desde el Creator (Punto 1 y 3)
//    * Esta función se pasa al hijo. Cuando el hijo quiera "guardar", 
//    * llamamos a la BL, guardamos en BD y recargamos la lista.
//    */
//   const onSongsChanged = async (updatedSongsListOrSingleSong, newSelectedId) => {
//     // NOTA IMPORTANTE: 
//     // Como estamos migrando de localStorage (donde pasábamos toda la lista) a BD,
//     // debemos asumir que el 'KaraokeSongCreator' nos está pasando la canción que se quiere guardar.
//     // Si tu componente Creator aún devuelve la lista completa, tomamos la que coincida con newSelectedId.
    
//     let songToSave = null

//     if (Array.isArray(updatedSongsListOrSingleSong)) {
//         // Si el creador devuelve array, buscamos la modificada
//         songToSave = updatedSongsListOrSingleSong.find(s => s.id === newSelectedId)
//     } else {
//         // Si ya devuelve el objeto canción directo
//         songToSave = updatedSongsListOrSingleSong
//     }

//     if (songToSave) {
//         // 1. Guardar en Base de Datos
//         const response = await BLSaveSong(songToSave)
        
//         if (response.success) {
//             showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Canción guardada correctamente.' })
            
//             // 2. Recargar lista desde BD para actualizar UI inmediatamente (Punto 3 - Arreglo)
//             await loadSongs()
            
//             // 3. Mantener selección
//             if (newSelectedId) setSelectedSongId(newSelectedId)
//         } else {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: response.message })
//         }
//     }
//   }

//   /** Resetear player al cambiar canción */
//   useEffect(() => {
//     stopPlayback()
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [selectedSongId])

//   // -- CONTROLES DEL PLAYER --
//   const stopPlayback = () => {
//     setIsPlaying(false)
//     setActiveBlockIndex(0)
//     setElapsedInBlockMs(0)
//     lastFrameTimeRef.current = 0
//     if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current)
//   }

//   const playPlayback = () => {
//     if (!blocks.length) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Canción vacía' })
//     setIsPlaying(true)
//   }

//   const pausePlayback = () => {
//     setIsPlaying(false)
//     lastFrameTimeRef.current = 0
//   }

//   const goToNextBlock = () => {
//     if (!blocks.length) return
//     setElapsedInBlockMs(0)
//     setActiveBlockIndex(prev => (prev + 1) >= blocks.length ? 0 : prev + 1)
//   }

//   const goToPrevBlock = () => {
//     if (!blocks.length) return
//     setElapsedInBlockMs(0)
//     setActiveBlockIndex(prev => (prev - 1) < 0 ? blocks.length - 1 : prev - 1)
//   }

//   /** LOOP DE ANIMACIÓN */
//   useEffect(() => {
//     if (!isPlaying || !activeBlock) return

//     const tick = (now) => {
//       if (!lastFrameTimeRef.current) lastFrameTimeRef.current = now
//       const delta = now - lastFrameTimeRef.current
//       lastFrameTimeRef.current = now

//       setElapsedInBlockMs(prev => {
//         const duration = getBlockDurationMs(activeBlock)
//         const nextElapsed = prev + delta

//         if (duration > 0 && nextElapsed >= duration) {
//           const nextIdx = activeBlockIndex + 1
          
//           if (nextIdx >= blocks.length) {
//             setIsPlaying(false) 
//             return duration     
//           }
          
//           setActiveBlockIndex(nextIdx)
//           lastFrameTimeRef.current = 0 
//           return 0 
//         }
//         return nextElapsed
//       })

//       rafIdRef.current = requestAnimationFrame(tick)
//     }

//     rafIdRef.current = requestAnimationFrame(tick)
//     return () => cancelAnimationFrame(rafIdRef.current)
//   }, [isPlaying, activeBlockIndex, activeBlock, blocks.length])

//   // -- RENDER --
//   return (
//     <SecondaryBase urlToGo={'/admin/dashboard'}>
//         <div className='LyricsGenerator'>
//             <p className='LyricsGenerator__Text--Title'>Karaoke</p>

//             {/* 1. BARRA DE CONTROLES */}
//             <div className='LyricsGenerator__Playlist'>
//                 <p className='LyricsGenerator__Text--Center'>Canción:</p>
//                 <select className='LyricsGenerator__Playlist__List' value={selectedSongId || ""} onChange={(e) => setSelectedSongId(e.target.value)}>
//                     <option value="">-- Selecciona una canción --</option>
//                     {songs.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
//                 </select>
//             </div>

//             {/* 2. PREVIEW VISUAL (PANTALLA) */}
//             <div className='LyricsGenerator__Player__LyricsContainer'>
//                 <div className='LyricsGenerator__Player__LyricsContainer__LyricsSubcontainer'>
//                     {/* Texto Base */}
//                     <div className='LyricsGenerator__Player__LyricsContainer__LyricsSubcontainer__BaseLyrics'>
//                         {displayText}
//                     </div>

//                     {/* Texto Relleno */}
//                     <div className='LyricsGenerator__Player__LyricsContainer__LyricsSubcontainer__FillLyrics' style={{ width: `${(fillProgress * 100).toFixed(2)}%` }}>
//                         {displayText}
//                     </div>
//                 </div>
//             </div>

//             {/* 3. INFO Y RELOJ */}
//             <div className='LyricsGenerator__Player__Data'>
//                 <div>Bloque: {blocks.length ? `${activeBlockIndex + 1} / ${blocks.length}` : "-"}</div>
//                 <div>{formatTime(currentPositionMs)} / {formatTime(totalDurationMs)}</div>
//             </div>

//             <div className='LyricsGenerator__Player__Buttons'>
//                 <button className='Btn__Adsl--Disabled LyricsGenerator__Player__Buttons__Button'><i className='bi bi-chevron-bar-left'></i> Inicio</button>
//                 <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={goToPrevBlock}><i className='bi bi-chevron-left'></i> Anterior</button>
//                 {!isPlaying ? (
//                     <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={playPlayback}><i className='bi bi-play'></i> Reproducir</button>
//                 ) : (
//                     <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={pausePlayback}><i className='bi bi-pause'></i> Pausa</button>
//                 )}
//                 <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={stopPlayback}><i className='bi bi-stop'></i> Detener</button>
//                 <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={goToNextBlock}><i className='bi bi-chevron-right'></i> Siguiente</button>
//                 <button className='Btn__Adsl--Disabled LyricsGenerator__Player__Buttons__Button'><i className='bi bi-chevron-bar-right'></i> Fin</button>
//             </div>

//             {isCreatorOpen ? (
//                 <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button--Center LyricsGenerator__SpaceUp' onClick={() => setIsCreatorOpen(false)}>Cerrar editor</button>
//             ) : (
//                 <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button--Center LyricsGenerator__SpaceUp' onClick={() => setIsCreatorOpen(true)}>Abrir editor</button>
//             )}

//             {/* 4. MODAL DEL CREADOR */}
//             {/* Aquí pasamos el onSongsChanged que ahora guarda en BD y refresca */}
//             {isCreatorOpen && (
//                 <div className='LyricsGenerator__Editor'>
//                     <KaraokeSongCreator 
//                         songs={songs} 
//                         selectedSongId={selectedSongId} 
//                         onSongsChanged={onSongsChanged} 
//                         onRequestClose={() => setIsCreatorOpen(false)} 
//                         generateUuid={generateUuid} 
//                     />
//                 </div>
//             )}
//         </div>
//     </SecondaryBase>
//   )
// }

// export { LyricsGenerator };
































// /**
//  * ==============================================================================
//  * CONFIGURACIÓN & UTILIDADES
//  * ==============================================================================
//  */

// // Generador de UUID para nuevas canciones (necesario para enviarlo al Backend)
// const generateUuid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `uuid_${Date.now()}_${Math.random()}`)

// // -- Helpers de Cálculo de Tiempos (Lógica de reproducción) --

// // Calcula la duración total de un bloque sumando sus partes o tomando el valor directo
// const getBlockDurationMs = (block) => {
//   if (!block) return 0
//   if (block.type === "instrumental" || block.mode === "uniform") return Math.max(0, Number(block.durationMs) || 0)
//   // Si es Parts, sumamos las partes individuales
//   return (block.parts || []).reduce((acc, p) => acc + Math.max(0, Number(p.durationMs) || 0), 0)
// }

// // Construye el texto visible plano para el preview (concatena partes si es necesario)
// const getBlockDisplayText = (block) => {
//   if (!block) return ""
//   if (block.type === "instrumental") return block.display === "icon" ? "♪" : ""
//   if (block.mode === "uniform") return block.text || ""
//   return (block.parts || []).map(p => p.text || "").join("")
// }

// // Calcula el porcentaje de relleno (0.0 a 1.0) para la barra de progreso visual
// const getFillProgress = (block, elapsedMs) => {
//   const total = getBlockDurationMs(block)
//   if (total <= 0) return 0
//   if (block.type === "instrumental") return 1 // Instrumental se muestra lleno o sin barra según diseño
//   return Math.min(Math.max(elapsedMs, 0), total) / total 
// }

// // Formatea milisegundos a MM:SS para el reloj
// const formatTime = (ms) => {
//   if (!ms || ms < 0) return "00:00"
//   const s = Math.floor(ms / 1000)
//   return `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`
// }

// /**
//  * ==============================================================================
//  * COMPONENTE PRINCIPAL: LYRICS GENERATOR
//  * ------------------------------------------------------------------------------
//  * Administrador de canciones y reproductor local para pruebas.
//  * ==============================================================================
//  */
// const LyricsGenerator = ({ getOutOfTheDynamic }) => {
//   const navigate = useNavigate()
//   const { showMessage } = useFloatMessage()

//   // -- ESTADOS DE DATOS --
//   const [songs, setSongs] = useState([]) // Lista de canciones traída de la BD
//   const [selectedSongId, setSelectedSongId] = useState(null)
//   const [isCreatorOpen, setIsCreatorOpen] = useState(false)

//   // -- ESTADOS DEL PLAYER --
//   const [isPlaying, setIsPlaying] = useState(false)
//   const [activeBlockIndex, setActiveBlockIndex] = useState(0)
//   const [elapsedInBlockMs, setElapsedInBlockMs] = useState(0)

//   // -- REFS PARA ANIMACIÓN --
//   const rafIdRef = useRef(null)
//   const lastFrameTimeRef = useRef(0)

//   // -- COMPUTADOS (MEMOIZACIÓN PARA RENDIMIENTO) --
  
//   // Encuentra la canción seleccionada en el array
//   const selectedSong = useMemo(() => songs.find(s => s.id === selectedSongId), [songs, selectedSongId])
  
//   // Extrae los bloques (letras) de la canción seleccionada
//   const blocks = useMemo(() => selectedSong?.lyrics || [], [selectedSong])
  
//   // Bloque actual en reproducción
//   const activeBlock = blocks[activeBlockIndex] || null
  
//   // Texto y progreso para renderizar
//   const displayText = useMemo(() => getBlockDisplayText(activeBlock), [activeBlock])
//   const fillProgress = getFillProgress(activeBlock, elapsedInBlockMs)

//   // Cálculos para el reloj de tiempo total
//   const totalDurationMs = useMemo(() => blocks.reduce((acc, b) => acc + getBlockDurationMs(b), 0), [blocks])
//   const currentPositionMs = useMemo(() => {
//     let prev = 0
//     for (let i = 0; i < activeBlockIndex; i++) prev += getBlockDurationMs(blocks[i])
//     return prev + elapsedInBlockMs
//   }, [blocks, activeBlockIndex, elapsedInBlockMs])

//   /**
//    * Carga inicial de canciones desde la Base de Datos
//    */
//   useEffect(() => {
//     loadSongsFromApi()
    
//     // Cleanup: Detener animación al desmontar
//     return () => { if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current) }
//   }, [])

//   /**
//    * Función asíncrona para obtener el listado actualizado de la API
//    */
//   const loadSongsFromApi = async () => {
//     try {
//       const response = await fetch(`${process.env.REACT_APP_API_URL}karaoke/songs`)
//       if (response.ok) {
//         const data = await response.json()
//         const songList = data.songs || []
//         setSongs(songList)

//         // Si hay canciones y no hay nada seleccionado, seleccionar la primera por defecto (opcional)
//         if (songList.length > 0 && !selectedSongId) {
//             setSelectedSongId(songList[0].id)
//         }
//       } else {
//         console.error("Error al obtener canciones de la BD")
//         showMessage({ type: MESSAGE_TYPE.BAD, text: 'No se pudieron cargar las canciones' })
//       }
//     } catch (error) {
//       console.error("Error de red:", error)
//     }
//   }

//   /**
//    * Callback ejecutado por KaraokeSongCreator cuando se guarda/edita/elimina una canción.
//    * @param {Array} _ignoredLocalList - Lista local que devuelve el hijo (la ignoramos para usar la fuente de verdad de la BD)
//    * @param {string} newSelectedId - ID de la canción que se acaba de crear/editar (para seleccionarla automáticamente)
//    */
//   const onSongsChanged = async (_ignoredLocalList, newSelectedId) => {
//     // 1. Recargamos la lista desde la BD para asegurar consistencia
//     await loadSongsFromApi()

//     // 2. Si nos mandaron un ID nuevo (creación/edición), lo seleccionamos
//     if (newSelectedId) {
//       setSelectedSongId(newSelectedId)
//     }
//   }

//   /** Resetear player al cambiar canción para evitar errores de índice */
//   useEffect(() => {
//     stopPlayback()
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [selectedSongId])

//   // -- CONTROLES DEL PLAYER (LÓGICA DE REPRODUCCIÓN) --

//   const stopPlayback = () => {
//     setIsPlaying(false)
//     setActiveBlockIndex(0)
//     setElapsedInBlockMs(0)
//     lastFrameTimeRef.current = 0
//     if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current)
//   }

//   const playPlayback = () => {
//     if (!blocks.length) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Canción vacía' })
//     setIsPlaying(true)
//   }

//   const pausePlayback = () => {
//     setIsPlaying(false)
//     lastFrameTimeRef.current = 0
//   }

//   const goToNextBlock = () => {
//     if (!blocks.length) return
//     setElapsedInBlockMs(0)
//     setActiveBlockIndex(prev => (prev + 1) >= blocks.length ? 0 : prev + 1)
//   }

//   const goToPrevBlock = () => {
//     if (!blocks.length) return
//     setElapsedInBlockMs(0)
//     setActiveBlockIndex(prev => (prev - 1) < 0 ? blocks.length - 1 : prev - 1)
//   }

//   /** * LOOP DE ANIMACIÓN (TICK)
//    * Gestiona el avance del tiempo y el cambio automático de bloques
//    */
//   useEffect(() => {
//     if (!isPlaying || !activeBlock) return

//     const tick = (now) => {
//       if (!lastFrameTimeRef.current) lastFrameTimeRef.current = now
//       const delta = now - lastFrameTimeRef.current
//       lastFrameTimeRef.current = now

//       setElapsedInBlockMs(prev => {
//         const duration = getBlockDurationMs(activeBlock)
//         const nextElapsed = prev + delta

//         // Si se acabó el tiempo del bloque actual
//         if (duration > 0 && nextElapsed >= duration) {
//           const nextIdx = activeBlockIndex + 1
          
//           if (nextIdx >= blocks.length) {
//             setIsPlaying(false) // Fin de canción
//             return duration     // Dejar lleno
//           }
          
//           // Avanzar al siguiente bloque y resetear timer frame
//           setActiveBlockIndex(nextIdx)
//           lastFrameTimeRef.current = 0 
//           return 0 // Resetear elapsed del nuevo bloque
//         }
//         return nextElapsed
//       })

//       rafIdRef.current = requestAnimationFrame(tick)
//     }

//     rafIdRef.current = requestAnimationFrame(tick)
//     return () => cancelAnimationFrame(rafIdRef.current)
//   }, [isPlaying, activeBlockIndex, activeBlock, blocks.length]) 

//   const ReturnToDashboard = async () => {
//     try {
//         // Lógica de salida si es necesaria
//     } catch (error) { }
//   }

//   // -- RENDERIZADO --
//   return (
//     <SecondaryBase urlToGo={'/admin/dashboard'}>
//         <div className='LyricsGenerator'>
//             <p className='LyricsGenerator__Text--Title'>Karaoke</p>

//             {/* 1. BARRA DE CONTROLES Y SELECCIÓN */}
//             <div className='LyricsGenerator__Playlist'>
//                 <p className='LyricsGenerator__Text--Center'>Canción:</p>
//                 <select className='LyricsGenerator__Playlist__List' value={selectedSongId || ""} onChange={(e) => setSelectedSongId(e.target.value)}>
//                     <option value="">-- Selecciona una canción --</option>
//                     {songs.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
//                 </select>
//             </div>

//             {/* 2. PREVIEW VISUAL (PANTALLA LOCAL) */}
//             <div className='LyricsGenerator__Player__LyricsContainer'>
//                 <div className='LyricsGenerator__Player__LyricsContainer__LyricsSubcontainer'>
                    
//                     {/* Texto Base (Inactivo) */}
//                     <div className='LyricsGenerator__Player__LyricsContainer__LyricsSubcontainer__BaseLyrics'>
//                         {displayText}
//                     </div>

//                     {/* Texto Relleno (Animado) */}
//                     <div className='LyricsGenerator__Player__LyricsContainer__LyricsSubcontainer__FillLyrics' style={{ width: `${(fillProgress * 100).toFixed(2)}%` }}>
//                         {displayText}
//                     </div>
//                 </div>
//             </div>

//             {/* 3. INFO Y RELOJ */}
//             <div className='LyricsGenerator__Player__Data'>
//                 <div>Bloque: {blocks.length ? `${activeBlockIndex + 1} / ${blocks.length}` : "-"}</div>
//                 <div>{formatTime(currentPositionMs)} / {formatTime(totalDurationMs)}</div>
//             </div>

//             {/* 4. BOTONES DE CONTROL */}
//             <div className='LyricsGenerator__Player__Buttons'>
//                 <button className='Btn__Adsl--Disabled LyricsGenerator__Player__Buttons__Button'><i className='bi bi-chevron-bar-left'></i> Inicio</button>
//                 <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={goToPrevBlock}><i className='bi bi-chevron-left'></i> Anterior</button>
//                 {!isPlaying ? (
//                     <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={playPlayback}><i className='bi bi-play'></i> Reproducir</button>
//                 ) : (
//                     <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={pausePlayback}><i className='bi bi-pause'></i> Pausa</button>
//                 )}
//                 <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={stopPlayback}><i className='bi bi-stop'></i> Detener</button>
//                 <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={goToNextBlock}><i className='bi bi-chevron-right'></i> Siguiente</button>
//                 <button className='Btn__Adsl--Disabled LyricsGenerator__Player__Buttons__Button'><i className='bi bi-chevron-bar-right'></i> Fin</button>
//             </div>

//             {/* BOTÓN PARA ABRIR/CERRAR EDITOR */}
//             {isCreatorOpen ? (
//                 <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button--Center LyricsGenerator__SpaceUp' onClick={() => setIsCreatorOpen(false)}>Cerrar editor</button>
//             ) : (
//                 <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button--Center LyricsGenerator__SpaceUp' onClick={() => setIsCreatorOpen(true)}>Abrir editor</button>
//             )}

//             {/* 5. EDITOR DE CANCIONES (COMPONENTE HIJO) */}
//             {isCreatorOpen && (
//                 <div className='LyricsGenerator__Editor'>
//                     <KaraokeSongCreator 
//                         songs={songs} 
//                         selectedSongId={selectedSongId} 
//                         onSongsChanged={onSongsChanged} 
//                         onRequestClose={() => setIsCreatorOpen(false)} 
//                         generateUuid={generateUuid} 
//                     />
//                 </div>
//             )}
//         </div>
//     </SecondaryBase>
//   )
// }

// export { LyricsGenerator }



























// /**
//  * ==============================================================================
//  * CONFIGURACIÓN & UTILIDADES
//  * ==============================================================================
//  */
// const LOCAL_STORAGE_KEY = "karaoke_songs_v1";

// const generateUuid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `uuid_${Date.now()}_${Math.random()}`);

// // -- Persistencia LocalStorage --
// const readSongs = () => {
//   try { return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY))?.songs || []; } 
//   catch { return []; }
// };

// const writeSongs = (songs) => {
//   localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ version: 1, songs }));
// };

// const createSampleSong = () => ({
//   id: generateUuid(), name: "Canción Demo", artist: "Sistema", url: "", lyrics: [
//     { id: generateUuid(), type: "lyric", mode: "uniform", text: "Bienvenido al Karaoke", durationMs: 3000 },
//     { id: generateUuid(), type: "lyric", mode: "uniform", text: "Selecciona una canción", durationMs: 2500 }
//   ]
// });

// // -- Helpers de Cálculo de Tiempos --
// const getBlockDurationMs = (block) => {
//   if (!block) return 0;
//   if (block.type === "instrumental" || block.mode === "uniform") return Math.max(0, Number(block.durationMs) || 0);
//   // Si es Parts, sumamos las partes
//   return (block.parts || []).reduce((acc, p) => acc + Math.max(0, Number(p.durationMs) || 0), 0);
// };

// const getBlockDisplayText = (block) => {
//   if (!block) return "";
//   if (block.type === "instrumental") return block.display === "icon" ? "♪" : "";
//   if (block.mode === "uniform") return block.text || "";
//   return (block.parts || []).map(p => p.text || "").join("");
// };

// const getFillProgress = (block, elapsedMs) => {
//   const total = getBlockDurationMs(block);
//   if (total <= 0) return 0;
//   if (block.type === "instrumental") return 1; // Instrumental no tiene barra de progreso visual
//   return Math.min(Math.max(elapsedMs, 0), total) / total; // 0..1
// };

// const formatTime = (ms) => {
//   if (!ms || ms < 0) return "00:00";
//   const s = Math.floor(ms / 1000);
//   return `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;
// };

// /**
//  * ==============================================================================
//  * COMPONENTE PRINCIPAL: KARAOKE
//  * ------------------------------------------------------------------------------
//  * Player + Hub principal.
//  * ==============================================================================
//  */
// const LyricsGenerator = ({ getOutOfTheDynamic }) => {
//     const navigate = useNavigate()
//     const { showMessage } = useFloatMessage()

//   // -- ESTADOS DE DATOS --
//   const [songs, setSongs] = useState([]);
//   const [selectedSongId, setSelectedSongId] = useState(null);
//   const [isCreatorOpen, setIsCreatorOpen] = useState(false);

//   // -- ESTADOS DEL PLAYER --
//   const [isPlaying, setIsPlaying] = useState(false);
//   const [activeBlockIndex, setActiveBlockIndex] = useState(0);
//   const [elapsedInBlockMs, setElapsedInBlockMs] = useState(0);

//   // -- REFS PARA ANIMACIÓN --
//   const rafIdRef = useRef(null);
//   const lastFrameTimeRef = useRef(0);

//   // -- COMPUTADOS (MEMO) --
//   const selectedSong = useMemo(() => songs.find(s => s.id === selectedSongId), [songs, selectedSongId]);
//   const blocks = useMemo(() => selectedSong?.lyrics || [], [selectedSong]);
//   const activeBlock = blocks[activeBlockIndex] || null;
//   const displayText = useMemo(() => getBlockDisplayText(activeBlock), [activeBlock]);
//   const fillProgress = getFillProgress(activeBlock, elapsedInBlockMs);

//   // Cálculos para el RELOJ
//   const totalDurationMs = useMemo(() => blocks.reduce((acc, b) => acc + getBlockDurationMs(b), 0), [blocks]);
//   const currentPositionMs = useMemo(() => {
//     let prev = 0;
//     for (let i = 0; i < activeBlockIndex; i++) prev += getBlockDurationMs(blocks[i]);
//     return prev + elapsedInBlockMs;
//   }, [blocks, activeBlockIndex, elapsedInBlockMs]);

//   /** Carga inicial */
//   useEffect(() => {
//     const stored = readSongs();
//     if (stored.length) {
//       setSongs(stored);
//       setSelectedSongId(stored[0].id);
//     } else {
//       const sample = createSampleSong();
//       writeSongs([sample]);
//       setSongs([sample]);
//       setSelectedSongId(sample.id);
//     }
//     return () => { if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current); };
//   }, []);

//   /** Manejo de cambios desde el Creator */
//   const onSongsChanged = (nextSongs, newSelectedId) => {
//     writeSongs(nextSongs);
//     setSongs(nextSongs);
//     if (newSelectedId) setSelectedSongId(newSelectedId);
//     else if (!nextSongs.find(s => s.id === selectedSongId)) setSelectedSongId(nextSongs[0]?.id || null);
//   };

//   /** Resetear player al cambiar canción */
//   useEffect(() => {
//     stopPlayback();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [selectedSongId]);

//   // -- CONTROLES DEL PLAYER --
//   const stopPlayback = () => {
//     setIsPlaying(false);
//     setActiveBlockIndex(0);
//     setElapsedInBlockMs(0);
//     lastFrameTimeRef.current = 0;
//     if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
//   };

//   const playPlayback = () => {
//     if (!blocks.length) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Canción vacía' })
//     setIsPlaying(true);
//   };

//   const pausePlayback = () => {
//     setIsPlaying(false);
//     lastFrameTimeRef.current = 0;
//   };

//   const goToNextBlock = () => {
//     if (!blocks.length) return;
//     setElapsedInBlockMs(0);
//     setActiveBlockIndex(prev => (prev + 1) >= blocks.length ? 0 : prev + 1);
//   };

//   const goToPrevBlock = () => {
//     if (!blocks.length) return;
//     setElapsedInBlockMs(0);
//     setActiveBlockIndex(prev => (prev - 1) < 0 ? blocks.length - 1 : prev - 1);
//   };

//   /** * LOOP DE ANIMACIÓN (TICK)
//    * Corregido para evitar saltos de índice y condiciones de carrera
//    */
//   useEffect(() => {
//     if (!isPlaying || !activeBlock) return;

//     const tick = (now) => {
//       if (!lastFrameTimeRef.current) lastFrameTimeRef.current = now;
//       const delta = now - lastFrameTimeRef.current;
//       lastFrameTimeRef.current = now;

//       setElapsedInBlockMs(prev => {
//         const duration = getBlockDurationMs(activeBlock);
//         const nextElapsed = prev + delta;

//         // Si el bloque terminó
//         if (duration > 0 && nextElapsed >= duration) {
//           const nextIdx = activeBlockIndex + 1;
          
//           if (nextIdx >= blocks.length) {
//             setIsPlaying(false); // Fin de canción
//             return duration;     // Dejar lleno
//           }
          
//           // Avanzar al siguiente bloque y resetear timer frame
//           setActiveBlockIndex(nextIdx);
//           lastFrameTimeRef.current = 0; 
//           return 0; // Resetear elapsed del nuevo bloque
//         }
//         return nextElapsed;
//       });

//       rafIdRef.current = requestAnimationFrame(tick);
//     };

//     rafIdRef.current = requestAnimationFrame(tick);
//     return () => cancelAnimationFrame(rafIdRef.current);
//   }, [isPlaying, activeBlockIndex, activeBlock, blocks.length]); // Dependencias críticas para que el loop se recree correctamente al cambiar bloque


//   return (
//     <SecondaryBase urlToGo={'/admin/dashboard'}>
//         <div className='LyricsGenerator'>
//             <p className='LyricsGenerator__Text--Title'>Karaoke</p>

//             {/* 1. BARRA DE CONTROLES */}
//             <div className='LyricsGenerator__Playlist'>
//                 <p className='LyricsGenerator__Text--Center'>Canción:</p>
//                 <select className='LyricsGenerator__Playlist__List' value={selectedSongId || ""} onChange={(e) => setSelectedSongId(e.target.value)}>
//                     {songs.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
//                 </select>
//             </div>

//             {/* 2. PREVIEW VISUAL (PANTALLA) */}
//             <div className='LyricsGenerator__Player__LyricsContainer'>
//                 <div className='LyricsGenerator__Player__LyricsContainer__LyricsSubcontainer'>
//                     {/* Texto Base (Gris/Blanco) */}
//                     <div className='LyricsGenerator__Player__LyricsContainer__LyricsSubcontainer__BaseLyrics'>
//                         {displayText}
//                     </div>

//                     {/* Texto Relleno (Amarillo) - Recorte por width */}
//                     <div className='LyricsGenerator__Player__LyricsContainer__LyricsSubcontainer__FillLyrics' style={{ width: `${(fillProgress * 100).toFixed(2)}%` }}>
//                         {displayText}
//                     </div>
//                 </div>
//             </div>

//             {/* 3. INFO Y RELOJ */}
//             <div className='LyricsGenerator__Player__Data'>
//                 <div>Bloque: {blocks.length ? `${activeBlockIndex + 1} / ${blocks.length}` : "-"}</div>
//                 <div>{formatTime(currentPositionMs)} / {formatTime(totalDurationMs)}</div>
//             </div>

//             <div className='LyricsGenerator__Player__Buttons'>
//                 <button className='Btn__Adsl--Disabled LyricsGenerator__Player__Buttons__Button'><i className='bi bi-chevron-bar-left'></i> Inicio</button>
//                 <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={goToPrevBlock}><i className='bi bi-chevron-left'></i> Anterior</button>
//                 {!isPlaying ? (
//                     <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={playPlayback}><i className='bi bi-play'></i> Reproducir</button>
//                 ) : (
//                     <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={pausePlayback}><i className='bi bi-pause'></i> Pausa</button>
//                 )}
//                 <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={stopPlayback}><i className='bi bi-stop'></i> Detener</button>
//                 <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button' onClick={goToNextBlock}><i className='bi bi-chevron-right'></i> Siguiente</button>
//                 <button className='Btn__Adsl--Disabled LyricsGenerator__Player__Buttons__Button'><i className='bi bi-chevron-bar-right'></i> Fin</button>
//             </div>

//             {isCreatorOpen ? (
//                 <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button--Center LyricsGenerator__SpaceUp' onClick={() => setIsCreatorOpen(false)}>Cerrar editor</button>
//             ) : (
//                 <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Player__Buttons__Button--Center LyricsGenerator__SpaceUp' onClick={() => setIsCreatorOpen(true)}>Abrir editor</button>
//             )}

//             {/* 4. MODAL DEL CREADOR */}
//             {isCreatorOpen && (
//                 <div className='LyricsGenerator__Editor'>
//                     <KaraokeSongCreator songs={songs} selectedSongId={selectedSongId} onSongsChanged={onSongsChanged} onRequestClose={() => setIsCreatorOpen(false)} generateUuid={generateUuid} />
//                 </div>
//             )}
//         </div>
//     </SecondaryBase>
//   );
// };

// export { LyricsGenerator };